import React, { useEffect } from 'react';
import type { DefaultValues, FieldPath, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { FlexProps } from 'styled-system';

import type { FieldValue, OnFormChangeEvent, OnFormChangeHandler } from '../../types/form';
import { Flex } from '../Flex';
import type { FormFieldType } from './FormField';
import FormField from './FormField';

type FormValues<TFieldName extends string> = {
  [key in TFieldName]: FieldValue;
};

export type OnWatchedFieldChange<TFieldName extends string> = (
  field: TFieldName,
  value: Nullable<FieldValue>,
  methods: {
    trigger: UseFormTrigger<FormValues<TFieldName>>;
    setValue: UseFormSetValue<FormValues<TFieldName>>;
    triggerFieldIfDirty: (field: FieldPath<FormValues<TFieldName>>) => void;
  }
) => void;

interface DynamicFormProps<TFieldName extends string> {
  id: string;
  formGroups: Array<FormFieldType<TFieldName> | React.ReactNode>;
  ariaLabel?: string;
  triggerValidation?: number;
  fieldsToWatch?: TFieldName[];
  isDisabledWhileSubmitting?: boolean;
  triggerValidationOnMount?: boolean;
  onWatchedFieldChange?: OnWatchedFieldChange<TFieldName>;
  renderSubmitButton?: (props: {
    form: string;
    isDisabled: boolean;
    isLoading: boolean;
    isFormDirty: boolean;
    type: 'submit';
  }) => React.ReactElement;
  onFormChange?: OnFormChangeHandler<TFieldName>;
  onSubmit?: (data: FormValues<TFieldName>) => Promise<void>;
  formConfig?: Parameters<typeof useForm<FormValues<TFieldName>>>[0];
  formStyles?: FlexProps;
  shouldFocusOnValidation?: boolean;
}

const getDefaultValues = <TFieldName extends string>(
  formGroups: Array<FormFieldType<TFieldName>>
) =>
  formGroups.reduce(
    (acc, field) => ({
      ...acc,
      [field.name]: field.initialValue,
    }),
    {}
  );

const isFieldFormGroup = <TFieldName extends string>(
  field: FormFieldType<TFieldName> | React.ReactNode
): field is FormFieldType<TFieldName> => !!(field as FormFieldType<TFieldName>)?.name;

const Form = styled(Flex)<React.FormHTMLAttributes<HTMLFormElement>>({
  boxSizing: 'border-box',
  flexDirection: 'column',
});

// eslint-disable-next-line max-lines-per-function
const DynamicForm = <TFieldName extends string>({
  id,
  onSubmit,
  ariaLabel,
  formConfig,
  formGroups,
  formStyles,
  onFormChange,
  fieldsToWatch,
  triggerValidation,
  renderSubmitButton,
  onWatchedFieldChange,
  triggerValidationOnMount,
  isDisabledWhileSubmitting = false,
  shouldFocusOnValidation = false,
}: DynamicFormProps<TFieldName>) => {
  const { formState, ...methods } = useForm<FormValues<TFieldName>>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: getDefaultValues(formGroups.filter(isFieldFormGroup)) as DefaultValues<
      FormValues<TFieldName>
    >,
    ...formConfig,
  });

  const { isValidating, isValid, isDirty, isSubmitting } = formState;

  useEffect(() => {
    if (onFormChange && !isValidating) {
      const formData = methods.getValues();

      const formDataMap = new Map(
        Object.entries(formData)
      ) as OnFormChangeEvent<TFieldName>['formData'];

      onFormChange({
        formData: formDataMap,
        isValid,
        isDirty,
      });
    }
  }, [isValidating, isValid, isDirty]);

  useEffect(() => {
    let subscription: ReturnType<typeof methods.watch>;

    if (fieldsToWatch && fieldsToWatch.length > 0 && onWatchedFieldChange) {
      subscription = methods.watch((value, { name }) => {
        const fieldName = name as unknown as TFieldName;

        if (name && fieldsToWatch.includes(fieldName)) {
          const changedValue = value?.[fieldName];

          const triggerFieldIfDirty = (fieldToTrigger: FieldPath<FormValues<TFieldName>>) => {
            const { isDirty: isFieldToTriggerDirty } = methods.getFieldState(fieldToTrigger);
            if (isFieldToTriggerDirty) {
              methods.trigger(fieldToTrigger);
            }
          };

          onWatchedFieldChange(fieldName, changedValue ?? null, {
            setValue: methods.setValue,
            trigger: methods.trigger,
            triggerFieldIfDirty,
          });
        }
      });
    }

    return () => subscription?.unsubscribe();
  }, [fieldsToWatch, onWatchedFieldChange, methods.watch]);

  useEffect(() => {
    if (triggerValidationOnMount) {
      methods.trigger();
    }
  }, [triggerValidationOnMount, methods.trigger]);

  useEffect(() => {
    if (triggerValidation && triggerValidation > 0) {
      methods.trigger(undefined, { shouldFocus: shouldFocusOnValidation });
    }
  }, [triggerValidation, methods.trigger]);

  return (
    <FormProvider {...{ formState, ...methods }}>
      <Form
        id={id}
        as="form"
        noValidate
        aria-label={ariaLabel}
        {...(onSubmit ? { onSubmit: methods.handleSubmit(onSubmit) } : {})}
        {...formStyles}
      >
        {formGroups.map((field, index) =>
          isFieldFormGroup(field) ? (
            <FormField
              key={field.name}
              isLastField={index === formGroups.length - 1}
              isDisabledWhileSubmitting={isDisabledWhileSubmitting}
              {...field}
            />
          ) : (
            field
          )
        )}
      </Form>

      {renderSubmitButton?.({
        form: id,
        type: 'submit',
        isDisabled: !isValid,
        isFormDirty: isDirty,
        isLoading: isSubmitting,
      })}
    </FormProvider>
  );
};

export default DynamicForm;
